import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  forkJoin,
  map,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs'

import { DriverAssignHistoryReportData } from '@ti/contracts'
import { ApiService } from '@ti/core/api'
import { RangeMs } from '@ti/core/common/contracts'
import { completeStore } from '@ti/core/common/utils'

export class HistoricalDriverProvider {
  protected readonly destroy$ = new Subject<void>()

  protected readonly store$ = {
    data: new BehaviorSubject<DriverAssignHistoryReportData[]>([]),
    vehicleIds: new BehaviorSubject<string[]>([]),
    currentTime: new BehaviorSubject<number | undefined>(undefined),
    selectedRange: new BehaviorSubject<RangeMs | undefined>(undefined),
    isLoading: new BehaviorSubject<boolean>(false),
  }

  public readonly state$ = {
    /**
     * Resolves the active driver-link assignment for every requested vehicle at `currentTime`,
     * keyed by vehicleId. Consumers can map the assignment's driverId/driverName onto a full
     * driver record (or fall back to the id/name carried by the assignment itself).
     */
    driverAssignmentByVehicle: combineLatest([this.store$.data, this.store$.currentTime]).pipe(
      map(([data, currentTime]) => {
        const assignmentByVehicle = new Map<string, DriverAssignHistoryReportData>()
        if (!currentTime) return assignmentByVehicle

        data.forEach((item) => {
          const assignedAt = new Date(item.assignedAt).valueOf()
          const unassignedAt = item.unassignedAt ? new Date(item.unassignedAt).valueOf() : undefined
          const isActiveAtCurrentTime =
            assignedAt <= currentTime && (!unassignedAt || currentTime <= unassignedAt)

          if (isActiveAtCurrentTime) {
            assignmentByVehicle.set(item.vehicleId, item)
          }
        })

        return assignmentByVehicle
      }),
    ),
  }

  public setVehicles(vehicleIds: string[]) {
    this.store$.vehicleIds.next(vehicleIds)
  }

  public setSelectedRange(val: RangeMs) {
    this.store$.selectedRange.next(val)
  }

  public setCurrentTime(val: number) {
    this.store$.currentTime.next(val)
  }

  public constructor(protected readonly api: ApiService) {
    Promise.resolve()
      .then(() => this.init())
      .catch((e) => console.warn('Cannot init HistoricalDriverProvider', e))
  }

  protected init() {
    combineLatest([this.store$.vehicleIds, this.store$.selectedRange])
      .pipe(
        debounceTime(128),
        takeUntil(this.destroy$),
        tap(() => this.store$.isLoading.next(true)),
        switchMap(([vehicleIds, selectedRange]) => {
          if (!vehicleIds.length || !selectedRange) {
            return Promise.resolve([] as DriverAssignHistoryReportData[])
          }
          // The report endpoint filters by a single vehicle, so fetch each vehicle's link
          // history in parallel and merge the results into a single flat list.
          return forkJoin(
            vehicleIds.map((vehicleId) => this.loadData$(vehicleId, selectedRange)),
          ).pipe(map((historyPerVehicle) => historyPerVehicle.flat()))
        }),
        tap(() => this.store$.isLoading.next(false)),
      )
      .subscribe((history) => this.store$.data.next(history))
  }

  protected loadData$(vehicleId: string, selectedRange: RangeMs) {
    return this.api.fleet.reports.driverLinkHistory$({
      filter: {
        vehicleId,
        timestampFrom: selectedRange[0] / 1000,
        timestampTo: selectedRange[1] / 1000,
      },
    })
  }

  public destroy() {
    completeStore(this.store$)
    this.destroy$.next()
    this.destroy$.complete()
  }
}
